import { Alert, Button, Modal } from '@catering/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import type { JSX } from 'react';
import { type Resolver, useForm } from 'react-hook-form';

import { listCalendarEntries } from '../../../calendar/api';
import { listServiceTypes, searchAllMenus } from '../../../menus/api';
import { mapOrderError } from '../../../orders/order-api-errors';
import {
  changeRequestFormSchema,
  type ChangeRequestFormValues,
} from '../../../orders/change-request-form-schema';
import { OrderItemEditor, type OrderItemEditorRow } from './order-item-editor';

export interface ChangeRequestFormModalProps {
  /** The order's own service date, to resolve the day's published dish per position — the same positions the order was placed against. */
  serviceDate: string;
  defaultValues: ChangeRequestFormValues;
  submitting: boolean;
  error: unknown;
  onSubmit: (values: ChangeRequestFormValues) => void | Promise<void>;
  onClose: () => void;
}

/**
 * `order-change-requests/spec.md` → "Creación de solicitud de cambio
 * sobre pedido confirmado" (UI). `items` starts pre-filled with the
 * order's current lines (`OrderDetailPage` seeds `defaultValues` via
 * `toChangeRequestFormValues`) so requesting a change means editing the
 * existing lines, not starting from a blank order. Same position-based
 * editor as `OrderForm` (`rotating-menu/design.md` §4): one row per
 * position with a published dish for the order's `serviceDate`.
 */
export function ChangeRequestFormModal({
  serviceDate,
  defaultValues,
  submitting,
  error,
  onSubmit,
  onClose,
}: ChangeRequestFormModalProps): JSX.Element {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ChangeRequestFormValues, unknown, ChangeRequestFormValues>({
    resolver: zodResolver(changeRequestFormSchema) as unknown as Resolver<
      ChangeRequestFormValues,
      unknown,
      ChangeRequestFormValues
    >,
    defaultValues,
  });

  const calendarQuery = useQuery({
    queryKey: ['calendar-for-change-request', serviceDate],
    queryFn: () => listCalendarEntries({ from: serviceDate, to: serviceDate, isPublished: true }),
  });

  const serviceTypesQuery = useQuery({ queryKey: ['service-types'], queryFn: listServiceTypes });

  const menusQuery = useQuery({
    queryKey: ['menus-for-change-requests'],
    queryFn: () =>
      searchAllMenus({
        q: undefined,
        serviceTypeId: undefined,
        dietaryTagId: undefined,
        excludeAllergenId: undefined,
        status: 'ACTIVE',
      }),
  });

  const menusById = Object.fromEntries((menusQuery.data?.items ?? []).map((menu) => [menu.id, menu]));
  const serviceTypeNames = Object.fromEntries(
    (serviceTypesQuery.data ?? []).map((serviceType) => [serviceType.id, serviceType.name]),
  );

  const rows: OrderItemEditorRow[] | undefined = calendarQuery.data?.map((entry) => ({
    positionId: entry.positionId,
    positionName: entry.position.name,
    serviceTypeId: entry.position.serviceTypeId,
    serviceTypeName: serviceTypeNames[entry.position.serviceTypeId] ?? entry.position.serviceTypeId,
    displayOrder: entry.position.displayOrder,
    dishName: menusById[entry.menuId]?.name ?? entry.menuId,
  }));

  const errorMessage = mapOrderError(error);

  return (
    <Modal open onClose={onClose} title="Solicitar cambio" className="max-w-2xl">
      <form
        onSubmit={(event) => void handleSubmit(onSubmit)(event)}
        className="flex flex-col gap-6"
        noValidate
      >
        {errorMessage !== '' ? <Alert tone="danger">{errorMessage}</Alert> : null}

        <p className="text-sm text-slate-700">
          Editá las líneas del pedido tal como querés que queden. Un usuario de operaciones distinto
          de vos va a aprobar o rechazar el cambio.
        </p>

        <OrderItemEditor control={control} register={register} rows={rows} errors={errors.items} />
        {errors.items?.root?.message === undefined ? null : (
          <p role="alert" className="text-xs text-red-600">
            {errors.items.root.message}
          </p>
        )}

        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" icon={Send} loading={submitting}>
            Enviar solicitud
          </Button>
        </div>
      </form>
    </Modal>
  );
}
